import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Package, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const TYPE_LABELS = {
  one_time: 'Tek Sefer',
  subscription: 'Abonelik',
  dues: 'Aidat',
  donation: 'Bağış',
};
const PERIOD_LABELS = { once: 'Tek Sefer', monthly: 'Aylık', yearly: 'Yıllık' };

const inputCls = 'w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]';

const empty = { name: '', description: '', price_tl: 0, currency: 'TL', product_type: 'one_time', billing_period: 'once', allow_custom_amount: false, is_active: true, position: 0 };

export default function ProductManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/products').then(r => setItems(r.data)).catch(() => toast.error('Ürünler yüklenemedi')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setForm(empty); setEditing(null); setShowForm(false); };
  const handleEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '', description: p.description || '', price_tl: parseFloat(p.price_tl) || 0,
      currency: p.currency || 'TL', product_type: p.product_type || 'one_time',
      billing_period: p.billing_period || 'once',
      allow_custom_amount: p.allow_custom_amount === true || p.allow_custom_amount === 'true' || p.allow_custom_amount === 'True',
      is_active: p.is_active === true || p.is_active === 'true' || p.is_active === 'True',
      position: parseInt(p.position) || 0,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Ürün adı gerekli'); return; }
    setIsSubmitting(true);
    try {
      const payload = { ...form, price_tl: parseFloat(form.price_tl) || 0, position: parseInt(form.position) || 0 };
      if (editing) { await api.put(`/admin/products/${editing.id}`, payload); toast.success('Ürün güncellendi'); }
      else { await api.post('/admin/products', payload); toast.success('Ürün eklendi'); }
      reset(); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;
    try { await api.delete(`/admin/products/${id}`); toast.success('Ürün silindi'); load(); }
    catch { toast.error('Silme başarısız'); }
  };

  const isSub = form.product_type === 'subscription';

  return (
    <div data-testid="admin-product-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Ürün / Paket Yönetimi</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Abonelik, aidat ve bağış kalemlerini tanımlayın.</p>
        </div>
        <button onClick={() => { reset(); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] transition-colors" data-testid="product-add-btn">
          <Plus className="w-4 h-4" /> Yeni Ürün
        </button>
      </div>

      {showForm && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">{editing ? 'Ürünü Düzenle' : 'Yeni Ürün'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Ürün Adı *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inputCls} data-testid="product-name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Fiyat (TL)</label>
                <input type="number" step="0.01" min="0" value={form.price_tl} onChange={e => setForm(f => ({ ...f, price_tl: e.target.value }))} className={inputCls} data-testid="product-price" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Açıklama</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className={`${inputCls} resize-none`} data-testid="product-desc" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Tür</label>
                <select value={form.product_type} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, product_type: v, billing_period: v === 'subscription' ? (f.billing_period === 'once' ? 'monthly' : f.billing_period) : 'once' })); }} className={inputCls} data-testid="product-type">
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Periyot</label>
                <select value={form.billing_period} onChange={e => setForm(f => ({ ...f, billing_period: e.target.value }))} className={inputCls} disabled={!isSub} data-testid="product-period">
                  {Object.entries(PERIOD_LABELS).filter(([k]) => isSub ? k !== 'once' : k === 'once').map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Sıra</label>
                <input type="number" value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} className={inputCls} />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.allow_custom_amount} onChange={e => setForm(f => ({ ...f, allow_custom_amount: e.target.checked }))} className="rounded" data-testid="product-custom-amount" /> Serbest tutar (bağış)</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" data-testid="product-active" /> Aktif</label>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50" data-testid="product-save">{isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}</button>
              <button type="button" onClick={reset} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-card border border-border">
          <Package className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Henüz ürün tanımlanmadı.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="product-list">
          {items.map(p => {
            const active = p.is_active === true || p.is_active === 'true' || p.is_active === 'True';
            return (
              <div key={p.id} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border" data-testid="product-item">
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center shrink-0">
                  {p.product_type === 'subscription' ? <RefreshCw className="w-5 h-5 text-[hsl(var(--gold))]" /> : <Package className="w-5 h-5 text-[hsl(var(--gold))]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{p.name}</span>
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{TYPE_LABELS[p.product_type] || p.product_type}</span>
                    {p.product_type === 'subscription' && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{PERIOD_LABELS[p.billing_period]}</span>}
                    {!active && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">Pasif</span>}
                  </div>
                  {p.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{p.description}</p>}
                </div>
                <div className="text-sm font-semibold text-[hsl(var(--gold))] shrink-0">{p.price_tl} {p.currency}</div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => handleEdit(p)} className="p-2 rounded-lg hover:bg-secondary" data-testid="product-edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(p.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive" data-testid="product-delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
